import { MapWrapper } from '@openlayers/MapWrapper';

// ol imports
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OlDraw from 'ol/interaction/Draw';
import { Type as OlGeometryType } from 'ol/geom/Geometry';
import * as olStyle from 'ol/style';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import * as olExtent from 'ol/extent';
import OlOverlay from 'ol/Overlay';

export class DrawInteractionTool implements MapTool {
    geometryType: OlGeometryType = 'Polygon';

    drawLayerDefaultZIndex = 98;
    drawLayerActiveZIndex = 99;
    labelsLayerDefaultZIndex = 120;
    labelsLayerActiveZIndex = 121;

    geometryListener = null;

    finishedStyle = new olStyle.Style({
        fill: new olStyle.Fill({
            color: 'rgba(255, 120, 20, 0.33)',
        }),
        stroke: new olStyle.Stroke({
            color: '#FF581C',
            width: 1,
        }),
        image: new olStyle.Circle({
            radius: 7,
            fill: new olStyle.Fill({
                color: '#FC3',
            }),
        }),
    });

    inProgressStyle = new olStyle.Style({
        fill: new olStyle.Fill({
            color: 'rgba(255, 255, 255, 0.3)',
        }),
        stroke: new olStyle.Stroke({
            color: 'rgba(0, 0, 0, 0.5)',
            lineDash: [10, 10],
            width: 1,
        }),
        image: new olStyle.Circle({
            radius: 5,
            stroke: new olStyle.Stroke({
                color: 'rgba(0, 0, 0, 0.7)',
            }),
            fill: new olStyle.Fill({
                color: 'rgba(255, 255, 255, 0.2)',
            }),
        }),
    });

    lineClickArea = new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: [0, 0, 0, 0.01],
            width: 15,
        }),
    });

    drawLayer = new VectorLayer({
        // layer for finished drawings or additional features, not for in-progress drawing
        source: new VectorSource({ wrapX: false }),
    });

    drawInteraction: OlDraw;
    featureLabelFeaturesMap: Map<Feature, Feature[]> = new Map(); // track labels for each feature
    overlayLabelsMap: Map<Feature, OlOverlay[]> = new Map(); // track labels for each feature
    inProgressLabel: OlOverlay = null; // remove only this label when tool is deactivated, clear all when tool is disabled
    showSegmentLabels: boolean = true;

    labelsLayer = new VectorLayer({
        source: new VectorSource({ wrapX: false }),
        declutter: true,
    });

    constructor(
        public mapWrapper: MapWrapper,
        public onDrawEndCallback?: (feature: Feature) => void,
        public onDrawProgressCallback?: (feature: any) => void,
        public onDrawRemove?: (features: Feature) => void
    ) {}

    onDrawStart(e) {
        if (this.onDrawProgressCallback) {
            this.onDrawProgressCallback(e.feature);
        }
    }

    onDrawEnd(e?: { feature: Feature }) {
        this.removeInProgressLabel();
        if (e?.feature) {
            e.feature.set('finished', true);
        }
        if (this.onDrawEndCallback) {
            this.onDrawEndCallback(e?.feature);
        }
        document.querySelectorAll('.measure-label').forEach(el => ((el as HTMLDivElement).parentElement.style.pointerEvents = 'auto'));
    }

    activate() {
        if (!this.drawInteraction) {
            this.drawInteraction = new OlDraw({
                type: this.geometryType,
                source: this.drawLayer.getSource(),
                style: this.inProgressStyle,
                stopClick: true,
                condition: event => {
                    return (event.originalEvent as MouseEvent).button !== 2;
                },
            });

            this.drawInteraction.on('drawstart', this.onDrawStart.bind(this));
            this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));
        }
        this.mapWrapper.getInstance().addInteraction(this.drawInteraction);

        this.drawLayer.getSource().on('removefeature', event => {
            if (event.feature) {
                this.clearFeatureLabels(event.feature);
            }
        });

        this.drawLayer.setStyle([this.finishedStyle, this.lineClickArea]);
        this.mapWrapper.addLayer(this.drawLayer, this.drawLayerDefaultZIndex, 'tool-draw');
        this.mapWrapper.addLayer(this.labelsLayer, this.labelsLayerDefaultZIndex, 'tool-draw-labels');
        this.drawLayer.setZIndex(this.drawLayerActiveZIndex);
        this.labelsLayer.setZIndex(this.labelsLayerActiveZIndex);
        this.drawInteraction.setActive(true);
    }

    deactivate() {
        if (this.drawInteraction) {
            (this.drawInteraction.getOverlay().getSource() as VectorSource).forEachFeature(f => this.clearFeatureLabels(f));
            this.drawInteraction.setActive(false);
        }

        this.drawLayer.setZIndex(this.drawLayerDefaultZIndex);
        this.labelsLayer.setZIndex(this.labelsLayerDefaultZIndex);

        this.mapWrapper.getInstance().removeInteraction(this.drawInteraction);

        this.removeInProgressLabel();
    }

    clear(removeLayer = true) {
        this.featureLabelFeaturesMap.forEach((labels, feature) => this.clearFeatureLabels(feature));
        this.featureLabelFeaturesMap.clear();
        this.labelsLayer.getSource().clear();

        this.overlayLabelsMap.forEach((labels, feature) => this.clearFeatureLabels(feature));
        this.overlayLabelsMap.clear();

        if (this.onDrawRemove) {
            this.drawLayer.getSource().forEachFeature(f => {
                this.onDrawRemove(f);
            });
        }
        this.drawLayer.getSource().clear();
        if (removeLayer) {
            this.mapWrapper.removeLayer(this.drawLayer);
            this.mapWrapper.removeLayer(this.labelsLayer);
        }
    }

    getExtentBySource(sourceUuid: string) {
        let extent = olExtent.createEmpty();
        this.drawLayer
            .getSource()
            .getFeatures()
            .filter(f => f.get('sourceUuid') === sourceUuid)
            .forEach(f => {
                olExtent.extend(extent, f.getGeometry().getExtent());
            });
        return extent;
    }

    clearBySource(sourceUuid: string) {
        this.drawLayer.getSource().forEachFeature(f => {
            if (f.get('sourceUuid') === sourceUuid) {
                this.drawLayer.getSource().removeFeature(f);
                this.clearFeatureLabels(f);
            }
        });
    }

    createOverlayLabel(feature: Feature, altStyle: boolean = false): OlOverlay {
        const $textElem = $('<div class="measure-label no-break-text">');
        $textElem.css({
            'background-color': 'rgba(255, 255, 255, 0.5)',
            padding: '0px 4px',
            'border-radius': '3px',
            'font-size': altStyle ? '12px' : '14px',
        });

        const label = new OlOverlay({
            element: $textElem[0] as HTMLDivElement,
            positioning: altStyle ? 'center-center' : 'bottom-center',
            stopEvent: false,
        });

        if (feature) {
            if (!this.overlayLabelsMap.has(feature)) {
                this.overlayLabelsMap.set(feature, []);
            }
            this.overlayLabelsMap.get(feature).push(label);
        }

        return label;
    }

    updateInProgressLabel(text: string, coordinate: number[], altStyle: boolean = false) {
        if (!text || !coordinate) return;

        if (!this.inProgressLabel) {
            this.inProgressLabel = this.createOverlayLabel(null, altStyle);
            this.mapWrapper.getInstance().addOverlay(this.inProgressLabel);
        }

        const el = this.inProgressLabel.getElement() as HTMLDivElement;
        if (el) {
            el.innerText = text;
        }
        this.inProgressLabel.setPosition(coordinate);
    }

    removeInProgressLabel() {
        if (this.inProgressLabel) {
            this.mapWrapper.getInstance().removeOverlay(this.inProgressLabel);
            this.inProgressLabel = null;
        }
    }

    createLabelFeature(parentFeature: Feature, text: string, coordinate: number[], isSegmentLabel: boolean = false): Feature {
        const labelFeature = new Feature({
            geometry: new Point(coordinate),
        });

        const textStyle = new olStyle.Text({
            text: text,
            font: isSegmentLabel ? 'bold 12px Arial, sans-serif' : 'bold 14px Arial, sans-serif',
            fill: new olStyle.Fill({ color: '#000' }),
            backgroundFill: new olStyle.Fill({ color: 'rgba(255, 255, 255, 0.7)' }),
            padding: [2, 4, 2, 4],
            offsetY: isSegmentLabel ? 0 : -10,
            declutterMode: isSegmentLabel ? 'declutter' : 'obstacle',
        });

        labelFeature.setStyle(new olStyle.Style({ text: textStyle }));

        if (parentFeature) {
            labelFeature.set('measurementFeature', parentFeature);
            if (!this.featureLabelFeaturesMap.has(parentFeature)) {
                this.featureLabelFeaturesMap.set(parentFeature, []);
            }
            this.featureLabelFeaturesMap.get(parentFeature).push(labelFeature);
        }

        this.labelsLayer.getSource().addFeature(labelFeature);
        return labelFeature;
    }

    clearFeatureLabels(feature: Feature) {
        if (this.featureLabelFeaturesMap.has(feature)) {
            const labelFeatures = this.featureLabelFeaturesMap.get(feature);
            labelFeatures.forEach(lf => {
                this.labelsLayer.getSource().removeFeature(lf);
            });
            this.featureLabelFeaturesMap.delete(feature);
        }
        if (this.overlayLabelsMap.has(feature)) {
            const labels = this.overlayLabelsMap.get(feature);
            labels.forEach(l => {
                this.mapWrapper.getInstance().removeOverlay(l);
            });
            this.overlayLabelsMap.delete(feature);
        }
    }

    cancelCurrentDrawing() {
        const countBefore = this.drawLayer.getSource().getFeatures().length;

        try {
            // OL bug, but should work anyway
            if (this.drawInteraction.getActive()) {
                this.drawInteraction.finishDrawing();
            }
        } catch (e) {}

        const src = this.drawLayer.getSource();
        const features = src.getFeatures();

        if (features.length > countBefore) {
            const feature = features[features.length - 1];
            src.removeFeature(feature);
        }
    }

    public getDrawingsCount(): number {
        if (!this.drawLayer || !this.drawLayer.getSource) return 0;
        return this.drawLayer.getSource().getFeatures().length;
    }

    public removeMeasurement = (feature: Feature) => {
        if (!feature) return;
        this.drawLayer.getSource().removeFeature(feature);
        if (this.onDrawRemove) {
            this.onDrawRemove(feature);
        }
    };

    getFeatureAtCoordinate = (coordinate: number[]): Feature => {
        const features = this.mapWrapper.getInstance().getFeaturesAtPixel(this.mapWrapper.getInstance().getPixelFromCoordinate(coordinate), {
            hitTolerance: 3,
            layerFilter: layer => {
                return ['logpoint', 'pipe', 'model_point'].includes(layer.get('id'));
            },
        });

        if (features && features.length > 0) {
            const pointFeatures = features.filter(f => {
                return f.getGeometry().getType() === 'Point' && ($.isNumeric(f.get('data')?.z) || $.isNumeric(f.get('data')?.elevation));
            });
            return pointFeatures[0] as Feature;
        }

        return null;
    };

    formatLength = length => {
        let output = '';
        const unit = kuura.getUnit();

        if (kuura.getProjectProj4Code() === 'EPSG:4326') {
            output += '~';
        }
        if (unit.abbr === 'm' && length > 1000) {
            output += `${Math.round(length) / 1000} km`;
        } else {
            output += `${Math.round(length * 100) / 100} ${unit.abbr}`;
        }
        return output;
    };

    getOverlayLabels(): OlOverlay[] {
        const labels: OlOverlay[] = [];
        this.overlayLabelsMap.forEach((value, key) => {
            labels.push(...value);
        });
        return labels;
    }

    triggerChange = () => {
        this.drawLayer
            .getSource()
            .getFeatures()
            .forEach(f => f.getGeometry().dispatchEvent('change'));
    };
}
